import app from 'app';
import Marionette from 'backbone.marionette';
import {
  ref,
  query,
  orderByChild,
  startAt,
  onChildAdded,
} from 'firebase/database';

app.namespace.Controller.NotificationController = Marionette.Object.extend({
  verbose: true,

  initialize({ userId }) {
    this.path = `/users/${userId}/notifications`;
    this._handlers = new Map();
  },

  /**
   * @private
   * @param args
   */
  _log(...args) {
    this.verbose && console.log(...args);
  },

  /**
   * @public
   * set firebase listener
   */
  listen() {
    app.api.realTime.whenReady.then(({ fbdb }) => {
      const messageRef = query(
        ref(fbdb, this.path),
        orderByChild('timestamp'),
        startAt(app.initDate / 1e3)
      );

      onChildAdded(messageRef, (data) => {
        const val = data.val();
        if (serverProfile.environment !== 'prod') {
          this._log('NotificationController', val?.data);
        }
        this.dispatchEvent(val);
      });
    });
  },

  /**
   * @private
   * publish event to relevant subscribers in order of priority with propagation control
   *
   * @param msg {Object} firebase originated message in dynamic format
   * @param [options] {{eventOrigin}}
   */
  dispatchEvent(msg, options) {
    options = Object.assign(
      {
        eventOrigin: null,
      },
      options
    );

    let aCallSequence = [];

    // find relevant handlers
    for (const [fn, descriptor] of this._handlers) {
      if (descriptor.events.includes(msg.data.event)) {
        aCallSequence.push({ fn, descriptor });
      }
    }

    if (_.isEmpty(aCallSequence)) {
      this._log(
        `Event ${msg.data.event}@${options.eventOrigin} has no subscriber`
      );
      return;
    }

    aCallSequence = _.sortBy(
      aCallSequence,
      (a) => -(a.descriptor.priority || 0)
    );

    // noinspection JSCheckFunctionSignatures
    const e = new CustomEvent(String(msg.data.event), {
      bubbles: true,
      cancelable: true,
      detail: msg,
    });
    e._eventOrigin = options.eventOrigin;

    // call each one till the end or if someone with higher priority calls preventDefault
    for (const item of aCallSequence) {
      item.fn(e);
      if (e.defaultPrevented) {
        break;
      }
    }
  },

  /**
   * @public
   * Subscribe to array of event/s
   * @note Requires to call `unsubscribe` after usage
   *
   * @param fn {Function}
   * @param descriptor {{events, priority}}
   */
  subscribe(fn, descriptor) {
    if (this._handlers.has(fn)) {
      console.warn('nu-nu-nu you already subscribed');
    }

    this._handlers.set(fn, descriptor);
  },

  /**
   * @public
   * Unsubscribe handler (same handler that was passed to subscribe)
   * @param fn {Function}
   */
  unsubscribe(fn) {
    if (this._handlers.has(fn)) {
      this._handlers.delete(fn);
    }
  },

  /**
   * @public
   * @param fn {Function}
   */
  has(fn) {
    return this._handlers.has(fn);
  },

  /**
   * @public
   * Publish custom/manual event to subscribers
   * @note: Intended for manual events (not originated from firebase)
   * @note type could be one of ['ERROR', 'WARN', 'INFO']
   * @param options {{text, type}}
   */
  push(options) {
    options = Object.assign(
      {
        text: 'Placeholder',
        type: '',
        on: {
          close: _.noop,
        },
        trigger: {
          close: _.noop,
        },
        uniqueGroupName: false,
      },
      options
    );

    // manual message format:
    const msg = {
      data: Object.assign(options, {
        event: 'MANUAL_NOTIFICATION',
      }),
      message: null,
      timestamp: Date.now() / 1e3,
    };

    this.dispatchEvent(msg, { eventOrigin: 'MANUAL_NOTIFICATION' });
  },

  /**
   * remove any visible notification
   * @param notificationId
   */
  remove({ notificationId }) {
    this.push({ notificationId, remove: true });
  },

  test() {
    this.dispatchEvent({
      message: 'DONE',
      data: {
        event: 'VIDEO_PUBLISHED',
        storyId: '5a0c0b3ce4b032713469e4bc',
        storyThumbnailUrl:
          'd2lmusdeyyumtj.cloudfront.net/artifacts/stories/5a1c17f560b278c4af769034/5a1c17f560b278c4af769034-1920x1080_1_Nov_27_2017_13_53_15_poster.jpg?' +
          $.param({ _: Date.now().toString(36) }),
        storyTitle: 'Copy of v8',
      },
    });
    this.dispatchEvent({
      message: 'PANIC',
      data: {
        event: 'VIDEO_PUBLISHED',
        storyId: '5a0c0b3ce4b032713469e4bc',
        storyThumbnailUrl:
          'd2lmusdeyyumtj.cloudfront.net/artifacts/stories/5a1c1e9d60b278c4af76903b/5a1c1e9d60b278c4af76903b-720x1280_1_Nov_27_2017_14_19_07_poster.jpg?' +
          $.param({ _: Date.now().toString(36) }),
        storyTitle:
          'Very long title of the failed video Very long title of the failed video Very long title of the failed video Very long title of the failed video Very long title of the failed video Very long title of the failed video Very long title of the failed video',
      },
    });

    this.dispatchEvent({
      data: {
        event: 'STORYBOARD',
        storyId: '5a0c0b3ce4b032713469e4bc',
        // eslint-disable-next-line max-len
        // "storyThumbnailUrl": "wochittest3-a.akamaihd.net/artifacts/stories/5a1c344c60b278c4af769048/5a1c344c60b278c4af769048-1080x1080_1_Nov_27_2017_15_51_48_poster.jpg?"+ $.param({_: Date.now().toString(36)}),
        storyTitle: 'Copy of v8',
      },
    });

    this.dispatchEvent({
      message: 'DONE',
      data: {
        event: 'SOCIAL_UPLOAD_VIDEO_READY',
        storyId: '5a0c0b3ce4b032713469e4bc',
        storyThumbnailUrl:
          'assetsprod-a.akamaihd.net/33848571_paimages/33848571_paimages_THUMBNAIL_256x144.jpg',
        storyTitle: 'Copy of v8',
        socialVideoUrl: 'https://www.youtube.com/watch?v=VN_4YPGn02I',
        socialProvider: 'facebook', // one of ['facebook','youtube','twitter']
      },
    });

    this.dispatchEvent({
      message: 'ALIEN ATTACK',
      data: {
        event: 'SOCIAL_UPLOAD_VIDEO_READY',
        storyId: '5a0c0b3ce4b032713469e4bc',
        storyThumbnailUrl:
          'assetsprod-a.akamaihd.net/33848571_paimages/33848571_paimages_THUMBNAIL_256x144.jpg',
        storyTitle: 'Copy of v8',
        socialVideoUrl: null,
        socialProvider: 'facebook', // one of ['facebook','youtube','twitter']
      },
    });

    this.dispatchEvent({
      message: 'INFO',
      data: {
        event: 'COPY_LOCKED_STORY',
        lockedStoryUserName: 'Moshe Abulafia',
        storyId: '5a0c0b3ce4b032713469e4bc',
        storyThumbnailUrl:
          'assetsprod-a.akamaihd.net/33848571_paimages/33848571_paimages_THUMBNAIL_256x144.jpg',
        storyTitle: 'Copy of v8',
        socialVideoUrl: null,
        socialProvider: 'facebook', // one of ['facebook','youtube','twitter']
      },
    });

    for (let n = 0, N = 3; n < N; n++) {
      this.push(
        [
          { text: 'Success...' },
          {
            text: 'Error... <a href="http://example.org/">link</a>',
            type: 'ERROR',
          },
          { text: 'Warning...', type: 'WARN' },
          { text: 'Information...', type: 'INFO' },
        ][n % 3]
      );
    }

    this.push({
      type: 'INFO',
      text: `long long long long long long long long long long long long
                 long long long long long long long long long long long long 
                 long long long long long long long long long long long long
                 long long long long long long long long long long long long
                 long long message`,
    });
  },
});
